import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  message,
  Table,
  Tag,
  Typography,
  Space,
  Divider,
  Timeline,
  Modal,
  Empty,
  ConfigProvider,
  Tooltip
} from 'antd';
import {
  SendOutlined,
  HistoryOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import contactApi from '../../api/contactApi';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ContactAID = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [filteredRequests, setFilteredRequests] = useState([]);


  const brandColor = '#3C007A';
  const accentColor = '#FF8F00';

  const baseStyle = {
    fontFamily: "'Rubik', sans-serif",
    fontSize: '14px',
    fontWeight: 400,
    color: '#333'
  };

  const labelStyle = {
    fontFamily: "'Rubik', sans-serif",
    fontSize: '16px',
    fontWeight: 400,
    color: '#333',
    marginBottom: '6px'
  };

  useEffect(() => {
    fetchContactRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [requests, searchText, statusFilter, categoryFilter]);

  const fetchContactRequests = async () => {
    try {
      setLoadingRequests(true);
      const data = await contactApi.getMyContactRequests();
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      message.error('Failed to load request history');
    } finally {
      setLoadingRequests(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(request =>
        request.subject?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.message?.toLowerCase().includes(searchText.toLowerCase()) ||
        request._id?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(request => request.category === categoryFilter);
    }

    setFilteredRequests(filtered);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setCategoryFilter('all');
  };


  const onFinish = async (values) => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

      // Each persona stores its profile reference under a different key
      // (company, association, startup, incubator, investor, academia, consultant)
      const profileId = userInfo[userInfo.role] || userInfo.company;

      const requestData = {
        ...values,
        companyId: profileId
      };

      // console.log('Submitting request:', requestData);

      const response = await contactApi.submitContactRequest(requestData);

      if (response.success) {
        message.success('Your request has been submitted successfully!');
        form.resetFields();
        fetchContactRequests(); // Refresh the request list
      } else {
        const errorMsg = response.errors?.join(', ') || 'Failed to submit request';
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      const errorMsg = error.errors?.join(', ') || error.message || 'Failed to submit request';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'pending': 'warning',
      'in-progress': 'processing',
      'resolved': 'success',
      'closed': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'pending': <ClockCircleOutlined />,
      'in-progress': <SyncOutlined spin />,
      'resolved': <CheckCircleOutlined />,
      'closed': <CheckCircleOutlined />,
      'rejected': <CloseCircleOutlined />
    };
    return iconMap[status?.toLowerCase()] || <ClockCircleOutlined />;
  };

  const showRequestDetail = async (record) => {
    try {
      const data = await contactApi.getContactRequestById(record._id);
      setSelectedRequest(data.request);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('Failed to load request details');
    }
  };

  const columns = [
    {
      title: 'Request ID',
      dataIndex: '_id',
      key: '_id',
      width: 120,
      render: (id) => (
        <Text copyable style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px', color: brandColor }}>
          #{id?.slice(-6).toUpperCase()}
        </Text>
      )
    },

    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category) => (
        <Tag color="purple" style={{ fontFamily: "'Rubik', sans-serif" }}>
          {category}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => (
        <Tag
          icon={getStatusIcon(status)}
          color={getStatusColor(status)}
          style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}
        >
          {status?.toUpperCase() || 'PENDING'}
        </Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => (
        <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px', color: '#666' }}>
          {new Date(date).toLocaleDateString()}
        </Text>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showRequestDetail(record)}
          style={{ fontFamily: "'Rubik', sans-serif", color: brandColor }}
        >
          View
        </Button>
      )
    }
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 14
        }
      }}
    >
      <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
        <style>
          {`
            .ant-form-item {
              margin-bottom: 16px !important;
            }
            .ant-input,
            .ant-input-textarea textarea,
            .ant-select-selector {
              font-family: 'Rubik', sans-serif !important;
              font-size: 14px !important;
              transition: all 0.3s ease !important;
            }
            .ant-input-affix-wrapper {
              padding: 0 12px !important;
              display: flex !important;
              align-items: center !important;
            }
            .ant-input-affix-wrapper .ant-input {
              padding: 0 12px 0 8px !important;
              text-align: left !important;
            }
            .ant-input-prefix {
              margin-right: 10px !important;
              color: ${brandColor} !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              font-size: 16px !important;
            }
            .ant-input:focus,
            .ant-input-focused,
            .ant-input-textarea textarea:focus,
            .ant-select-focused .ant-select-selector {
              background-color: transparent !important;
              box-shadow: 0 0 0 2px rgba(60, 0, 122, 0.1) !important;
            }
            .ant-input::placeholder,
            .ant-input-textarea textarea::placeholder,
            .ant-select-selection-placeholder {
              font-family: 'Rubik', sans-serif !important;
              font-size: 14px !important;
              color: #999999 !important;
              text-align: left !important;
            }
            .ant-select-selection-item {
              text-align: left !important;
            }
            .ant-select-selector {
              padding: 0 12px !important;
              text-align: left !important;
            }
            .ant-select-selection-search {
              left: 0 !important;
            }
            .ant-select-arrow {
              color: ${brandColor} !important;
            }
            .contact-aid-card .ant-card-body {
              padding: 32px !important;
            }
            .submit-button {
              display: block !important;
              margin: 0 auto !important;
            }
            .ant-input-textarea textarea {
              text-align: left !important;
              padding: 12px !important;
            }
          `}
        </style>

        <Row gutter={[24, 24]}>
          {/* Contact Form */}
          <Col xs={24} lg={12}>
            <Card
              bordered={false}
              className="contact-aid-card"
              style={{
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
              }}
            >
              <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      fontSize: '24px',
                      color: brandColor
                    }}
                  >
                    {/* <SendOutlined style={{ marginRight: '12px' }} /> */}
                    Contact AID Office
                  </Title>

                </div>

                <Divider style={{ margin: '5px 0' }} />

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  requiredMark={false}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span style={labelStyle}>Your Name</span>}
                        name="contactName"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="Enter your full name"
                          style={{ height: '42px' }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span style={labelStyle}>Email Address</span>}
                        name="email"
                        rules={[
                          { required: true, message: 'Please enter email' },
                          { type: 'email', message: 'Please enter valid email' }
                        ]}
                      >
                        <Input
                          prefix={<MailOutlined />}
                          placeholder="Enter your email address"
                          style={{ height: '42px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={<span style={labelStyle}>Phone Number</span>}
                    name="phone"
                    rules={[{ required: true, message: 'Please enter phone number' }]}
                  >
                    <Input
                      prefix={<PhoneOutlined />}
                      placeholder="Enter your contact number"
                      style={{ height: '42px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={labelStyle}>Category</span>}
                    name="category"
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select
                      placeholder="Select request category"
                      style={{ height: '42px' }}
                      suffixIcon={<MessageOutlined style={{ color: brandColor }} />}
                    >
                      <Option value="General Inquiry">General Inquiry</Option>
                      <Option value="Technical Support">Technical Support</Option>
                      <Option value="Account Issue">Account Issue</Option>
                      <Option value="Product Information">Product Information</Option>
                      <Option value="Partnership">Partnership</Option>
                      <Option value="Complaint">Complaint</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label={<span style={labelStyle}>Subject</span>}
                    name="subject"
                    rules={[{ required: true, message: 'Please enter subject' }]}
                  >
                    <Input
                      prefix={<MessageOutlined />}
                      placeholder="Brief subject of your request"
                      style={{ height: '42px' }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={labelStyle}>Message</span>}
                    name="message"
                    rules={[
                      { required: true, message: 'Please enter your message' },
                      { min: 20, message: 'Message must be at least 20 characters' }
                    ]}
                  >
                    <TextArea
                      rows={6}
                      placeholder="Describe your request in detail..."
                      showCount
                      maxLength={1000}
                      style={{
                        textAlign: 'left',
                        padding: '12px'
                      }}
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0, textAlign: 'center', marginTop: '24px' }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      // icon={<SendOutlined />}
                      className="submit-button"
                      style={{
                        width: '40%',
                        maxWidth: '150px',
                        height: '46px',
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 500,
                        backgroundColor: brandColor,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(60, 0, 122, 0.2)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(60, 0, 122, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(60, 0, 122, 0.2)';
                      }}
                    >
                      Submit Request
                    </Button>
                  </Form.Item>
                </Form>
              </Space>
            </Card>
          </Col>

          {/* Request History */}
          <Col xs={24} lg={12}>
            <Card
              bordered={false}
              style={{
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '100%'
              }}
              bodyStyle={{ padding: '32px' }}
            >
              <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 400,
                      fontSize: '24px',
                      color: brandColor
                    }}
                  >
                    <HistoryOutlined style={{ marginRight: '12px' }} />
                    Request History
                  </Title>

                </div>

                <Divider style={{ margin: '5px 0' }} />


                <Row gutter={[12, 12]}>
                  <Col xs={24} md={10}>
                    <Input
                      placeholder="Search by subject, message, or ID"
                      prefix={<SearchOutlined style={{ color: brandColor }} />}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      allowClear
                      style={{
                        borderRadius: '8px',
                        height: '40px'
                      }}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      style={{ width: '100%', height: '40px' }}
                      suffixIcon={<FilterOutlined style={{ color: brandColor }} />}
                    >
                      <Option value="all">All Status</Option>
                      <Option value="pending">Pending</Option>
                      <Option value="in-progress">In Progress</Option>
                      <Option value="resolved">Resolved</Option>
                      <Option value="closed">Closed</Option>
                      <Option value="rejected">Rejected</Option>
                    </Select>
                  </Col>
                  <Col xs={12} md={6}>
                    <Select
                      value={categoryFilter}
                      onChange={handleCategoryFilterChange}
                      style={{ width: '100%', height: '40px' }}
                      suffixIcon={<FilterOutlined style={{ color: brandColor }} />}
                    >
                      <Option value="all">All Categories</Option>
                      <Option value="General Inquiry">General Inquiry</Option>
                      <Option value="Technical Support">Technical Support</Option>
                      <Option value="Account Issue">Account Issue</Option>
                      <Option value="Product Information">Product Information</Option>
                      <Option value="Partnership">Partnership</Option>
                      <Option value="Complaint">Complaint</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Col>
                  <Col xs={24} md={2}>
                    <Tooltip title="Refresh & Clear Filters">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          clearFilters();
                          fetchContactRequests();
                        }}
                        style={{
                          width: '100%',
                          height: '40px',
                          borderRadius: '8px'
                        }}
                      />
                    </Tooltip>
                  </Col>
                </Row>


                <Table
                  columns={columns}
                  dataSource={requests}
                  loading={loadingRequests}
                  rowKey="_id"
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    pageSize: 5,
                    showTotal: (total) => `Total ${total} requests`,
                    style: { fontFamily: "'Rubik', sans-serif" }
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        description={
                          <Text style={{ fontFamily: "'Rubik', sans-serif", color: '#999' }}>
                            No requests yet. Submit your first request!
                          </Text>
                        }
                      />
                    )
                  }}
                  style={{ fontFamily: "'Rubik', sans-serif" }}
                />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Request Detail Modal */}
        <Modal
          title={
            <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: '20px', fontWeight: 500, color: brandColor }}>
              Request Details
            </span>
          }
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={700}
          centered
          style={{ fontFamily: "'Rubik', sans-serif" }}
        >
          {selectedRequest && (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                  Request ID
                </Text>
                <br />
                <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: '15px', color: brandColor }}>
                  #{selectedRequest._id?.slice(-8).toUpperCase()}
                </Text>
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                    Category
                  </Text>
                  <br />
                  <Tag color="purple" style={{ marginTop: '4px', fontFamily: "'Rubik', sans-serif" }}>
                    {selectedRequest.category}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                    Status
                  </Text>
                  <br />
                  <Tag
                    icon={getStatusIcon(selectedRequest.status)}
                    color={getStatusColor(selectedRequest.status)}
                    style={{ marginTop: '4px', fontFamily: "'Rubik', sans-serif" }}
                  >
                    {selectedRequest.status?.toUpperCase()}
                  </Tag>
                </Col>
              </Row>

              <Divider />

              <div>
                <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: '15px' }}>
                  Subject
                </Text>
                <Paragraph style={{ fontFamily: "'Rubik', sans-serif", fontSize: '14px', margin: '8px 0' }}>
                  {selectedRequest.subject}
                </Paragraph>
              </div>

              <div>
                <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: '15px' }}>
                  Message
                </Text>
                <Paragraph style={{ fontFamily: "'Rubik', sans-serif", fontSize: '14px', margin: '8px 0', color: '#666' }}>
                  {selectedRequest.message}
                </Paragraph>
              </div>

              {selectedRequest.response && (
                <div style={{ backgroundColor: '#f0ebf8', padding: '16px', borderRadius: '8px' }}>
                  <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: '15px', color: brandColor }}>
                    AID Office Response
                  </Text>
                  <Paragraph style={{ fontFamily: "'Rubik', sans-serif", fontSize: '14px', margin: '8px 0 0 0' }}>
                    {selectedRequest.response}
                  </Paragraph>
                </div>
              )}

              <Divider />

              <Timeline
                items={[
                  {
                    color: 'green',
                    children: (
                      <div>
                        <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                          Request Submitted
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif", fontSize: '12px' }}>
                          {new Date(selectedRequest.createdAt).toLocaleString()}
                        </Text>
                      </div>
                    )
                  },
                  {
                    color: selectedRequest.status === 'pending' ? 'gray' : 'blue',
                    children: (
                      <div>
                        <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                          {selectedRequest.status === 'pending' ? 'Awaiting Review' : 'Under Review'}
                        </Text>
                      </div>
                    )
                  },
                  {
                    color: selectedRequest.status === 'resolved' ? 'green' : 'gray',
                    children: (
                      <div>
                        <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: '13px' }}>
                          {selectedRequest.status === 'resolved' ? 'Resolved' : 'Resolution Pending'}
                        </Text>
                        {selectedRequest.resolvedAt && (
                          <>
                            <br />
                            <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif", fontSize: '12px' }}>
                              {new Date(selectedRequest.resolvedAt).toLocaleString()}
                            </Text>
                          </>
                        )}
                      </div>
                    )
                  }
                ]}
              />
            </Space>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default ContactAID;
